import { BrowserSupport } from '@tableau/browser-support';
import { SignInUrlBuilder } from '@tableau/shared-urls';
import { ToastWidget } from '@tableau/widgets';
import * as _ from 'underscore';
import * as URI from 'uri-js';
import { setRootAdminUiState } from '../app/actions/spaces/RootAdminUiStateActions';
import { setPreviousUrl } from '../app/actions/spaces/SetPreviousUrlReducerAction';
import { getAppStore } from '../app/AppStore';
import { LoginUrlsWrapper } from '../app/spaces/Login/LoginUrls';
import { RootAdminUiState } from '../app/state/RootAdminUIState';
import { UrlParamKeys } from '../app/UrlParamKeys';
import { AuthenticationType } from '../settings/types/IServerSettingsUnauthenticated';
import { CommonErrorCode, UninitializedServerErrorCode } from './helpers/ErrorCodes';
import { Helpers } from './helpers/Helpers';
import { getSiteUrlNameIfOnSigninPath, getVizPath, isVizUrl, shouldSetPreviousUrl } from './helpers/UrlHelpers';
import { ImagePreloader } from './ImagePreloader';
import { L10n } from './L10n/L10n';
import { ModalService } from './services/ModalService';
import { ReactModalService } from '../commonInfra/dialogs/ReactModalService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../sessionManagement/SessionManagementService';
import { SessionService } from '../commonInfra/services/SessionService';
import { SiteSwitchService } from './services/SiteSwitchService';
import { TemplatePreloadService } from './services/TemplatePreloadService';
import { ToasterService } from './services/ToasterService';
import { VizService } from './services/VizService';
import { WindowPopupService } from './services/WindowPopupService';
import { WindowLocationService } from '../commonInfra/services/WindowLocationService';
import { ServerSettingsUnauthenticated } from '../settings/services/ServerSettingsUnauthenticated';
import { Messages } from '../codegen/Localize';
import { HelpService } from '../commonInfra/services/HelpService';
import { SizeFilter } from '../commonInfra/utils/SizeFilter';
import { DeprecatedSelectByServerEnvironment } from './filters/SelectByServerEnvironment';
import { getAuthenticationServiceInstance } from '../authentication/services/AuthenticationService';

/**
 * This is the run block for VizPortal. It is run once when the app is loaded.
 * We declare it in an isolated module for the reasons mentioned in Angular's
 * documentation (https://docs.angularjs.org/guide/module#run-blocks):
 *
 *    Run blocks are the closest thing in Angular to the main method.
 *    A run block is the code which needs to run to kickstart the application.
 *    It is executed after all of the service have been configured and the
 *    injector has been created. Run blocks typically contain code which is
 *    hard to unit-test, and for this reason should be declared in isolated
 *    modules, so that they can be ignored in the unit-tests.
 */
angular.module('VizPortalRun', ['VizPortal']).run(<any[]>[
  '$rootScope',
  '$window',
  '$document',
  '$interval',
  '$location',
  '$timeout',
  '$translate',
  'ModalService',
  'SiteSwitchService',
  'VizService',
  'WindowPopupService',
  'TemplatePreloadService',
  (
    $rootScope: ng.IRootScopeService,
    $window: ng.IWindowService,
    $document: ng.IDocumentService,
    $interval: ng.IIntervalService,
    $location: ng.ILocationService,
    $timeout: ng.ITimeoutService,
    $translate: ng.translate.ITranslateService,
    modalService: ModalService,
    SiteSwitchService: SiteSwitchService,
    VizService: VizService,
    WindowPopupService: WindowPopupService,
    TemplatePreloadService: TemplatePreloadService,
  ) => {
    const server = getSessionManagementServiceInstance();
    // Add Messages to the root scope as a temporary replacement of the $translate filter in pug templates
    ($rootScope as any).Messages = Messages;
    // Put the filters on root scope until angular to react migration is done
    ($rootScope as any).getServerHelpUrlByCategory = HelpService.getServerHelpUrlByCategory;
    ($rootScope as any).sizeFilter = SizeFilter;
    ($rootScope as any).localizedDate = L10n.localizedDate;
    ($rootScope as any).localizedDateTime = L10n.localizedDateTime;
    ($rootScope as any).localizedPercent = L10n.localizedPercent;
    ($rootScope as any).selectByServerEnvironment = DeprecatedSelectByServerEnvironment;

    // Defer prefetching until after the first locationChangeSuccess so
    // we don't do any extra work before the page is first rendered
    const removeImagePreloadListener = $rootScope.$on('$locationChangeSuccess', () => {
      $timeout(() => ImagePreloader.preloadDynamicIconImages(), 1000);
      removeImagePreloadListener();
    });

    const removeTemplatePreloadListener = $rootScope.$on('$locationChangeSuccess', () => {
      $timeout(() => TemplatePreloadService.preloadTemplates(), 1000);
      removeTemplatePreloadListener();
    });

    function redirectToLogin(errorCode: number) {
      const params: SignInUrlBuilder.QueryParams = {
        redirect: $location.url() || null,
        error: errorCode === CommonErrorCode.InvalidSession ? null : errorCode
      };
      if (getAuthenticationServiceInstance().isAuthenticationAutoLogin()) {
        // The signin state will attempt to auto-login by default. We don't want it to here.
        params.disableAutoSignin = true;
      }
      const siteUrlName = getSiteUrlNameIfOnSigninPath($location.path(), $location.search());
      if (siteUrlName) {
        params.site = siteUrlName;
      }
      LoginUrlsWrapper.navigateToSignIn(params);
    }

    function shouldRedirectToLogin(nextUrl: string) {
      const isNextStateAnonymous = getAuthenticationServiceInstance().isAnonymousUrl(nextUrl);
      const isNonVizPageWithRestrictedSession =
        server.isSessionRestrictedTrustedTicket() && nextUrl && !isVizUrl(nextUrl);
      return !isNextStateAnonymous && (!server.inSession() || isNonVizPageWithRestrictedSession);
    }

    function shouldAutoLogin(nextUrl: string) {
      const isNextStateAnonymous = getAuthenticationServiceInstance().isAnonymousUrl(nextUrl);
      return !server.inSession() && !isNextStateAnonymous && getAuthenticationServiceInstance().isAuthenticationAutoLogin();
    }

    function shouldRedirectToEmbeddedViz(nextUrl: string) {
      const isPhone = Math.min(screen.width, screen.height) <= ServerSettingsUnauthenticated.dsdPhoneSize();

      if (nextUrl && isVizUrl(nextUrl)) {
        return VizService.isEmbedParamOn() || server.isSessionRestrictedTrustedTicket() || isPhone;
      }

      return false;
    }

    function shouldRedirectToGuestViz(nextUrl: string) {
      return nextUrl && isVizUrl(nextUrl) && !server.inSession() && ServerSettingsUnauthenticated.isGuestEnabled();
    }

    function shouldRedirectToVizExport(nextUrl: string) {
      return nextUrl && isVizUrl(nextUrl) && VizService.pathHasExportableFileType();
    }

    function maybeVizRedirect(nextUrl: string, shouldRedirectWithoutAddingToHistory: boolean, event?: ng.IAngularEvent) {
      if (shouldRedirectToVizExport(nextUrl)) {
        WindowLocationService.navigateToWithoutAddingToHistory(VizService.getVizqlUrl());
        return true;
      } else if (shouldRedirectToEmbeddedViz(nextUrl)) {
        VizService.redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory, event, {shouldPreventDefault: true});
        return true;
      } else if (shouldRedirectToGuestViz(nextUrl)) {
        VizService.redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory, event, {isFromGuest: true});
        return true;
      }
      return false;
    }

    function shouldRedirectToSitePicker(props: {isSuccessfulSamlLogin: boolean}) {
      return props.isSuccessfulSamlLogin && server.isSessionMultiSite() && isRootUrl();
    }

    function isRootUrl() {
      return $location.path() === '' || $location.path() === '/';
    }

    // VizPortal 9.0 Session Expiration Solution - B144748
    // 1) Only the getActionInfo and getSessionInfo requests will extend your session
    // 2) As long as the user is “interacting ”, getSessionInfo will be requested once every 5 minutes or half
    //    the configured idle time limit (wgserver.session.idle_limit), whichever is less
    // 3) We determine if the user is “interacting” by listening to mousemove and keydown events in the browser
    function initializeSessionPing() {
      // If the browser tab comes into focus we want to refresh the session info.
      // Do this at most once every 15 seconds
      $($window).focus(_.throttle(() => server.refreshSessionInfo(true), 15000, {trailing: false}));

      const fiveMinutesInMilliseconds = 5 * 60 * 1000;
      const halfSessionIdleLimitInMilliseconds = server.getSessionIdleLimitSeconds() * 1000;
      let sessionPingPeriodWhileUserIsInteracting = Math.min(fiveMinutesInMilliseconds, halfSessionIdleLimitInMilliseconds);

      // Don't let the period fall below 15 seconds, because that's absurdly short
      sessionPingPeriodWhileUserIsInteracting = Math.max(sessionPingPeriodWhileUserIsInteracting, 15 * 1000);

      const listenForInteractionAfterSessionPingPeriod = () => {
        // timeout for session ping period, then listen to interaction events
        $interval(() => {
          // $.one will bind to the event once per element per event type. In this case the handler will
          // be fired twice if mousemove and keydown both fire, so ensure the handler is only called once
          $($document).one('keydown mousemove touchstart', _.once(() => {
            // User is alive! ping the server, then sleep on it
            server.refreshSessionInfo(true);
            listenForInteractionAfterSessionPingPeriod();
          }));
        }, sessionPingPeriodWhileUserIsInteracting, 1, false);
      };

      // Kick it off
      listenForInteractionAfterSessionPingPeriod();
    }

    // The client is hidden initially. We should show it once we know we will be displaying UI
    // (as opposed to navigating away from vizportal client).
    function showClient(rootAdminUiState: RootAdminUiState) {
      getAppStore().dispatch(setRootAdminUiState(rootAdminUiState));
      $($window.document.body).show();
      $rootScope.$applyAsync();
      // When the VizClient puts an element into fullscreen mode, Edge (for unknown reasons) retains the
      // VizPortal top bar. We work around that here by hiding the top bar in fullscreen mode. TFS 557632
      if (BrowserSupport.IsLegacyEdge()) {
        // TODO: Remove the "any" type assertion after https://github.com/Microsoft/TSJS-lib-generator/pull/597 is merged
        $($window.document).on('fullscreenchange', () => { $('.tb-top-bar').toggle(($window.document as any).fullscreenElement === null); });
      }
    }

    // Overrides the server authentication type with the uppercased "auth" param from the url
    // Example: http://localhost/#/signin?auth=external -> EXTERNAL
    // TFS:373719 Reimplement the allow_direct_access scenario for 9.0
    function overrideAuthenticationTypeBasedOnUrl() {
      const override: AuthenticationType = $location.search()[UrlParamKeys.AuthenticationTypeOverride];
      $location.search(UrlParamKeys.AuthenticationTypeOverride, null);
      const newAuthType = override ? override.toUpperCase() as AuthenticationType : null;
      getAuthenticationServiceInstance().overrideAuthenticationType(newAuthType);
    }

    function bootstrapCheckInitialAdmin(code: number, nextUrl: string) {
      // Defect 458853:[LC] Intermittent Calls to getSessionInfo clears workgroup_session_id, which makes data source authentication imposible
      // When user not authenticated on Tableau Server, data source authentication dialog
      // will get temporary guest session in order to client to encrypt credentials and server to descrypt them
      // But calls to getSessionInfo will clear session cookie. Disabling calls to getSessionInfo when requested.
      const fragment = URI.parse(nextUrl).fragment;
      if (typeof fragment === 'undefined' ||
        (!fragment.startsWith('/authenticate') &&
          !fragment.startsWith('/pkce') &&
          !fragment.startsWith('/signin'))) {
        // Initializing sesssion ping only when LazyConnections disabled or disableSessionPing was NOT explicitly requested in state params
        initializeSessionPing();
      }

      overrideAuthenticationTypeBasedOnUrl();
      // Need to know if the user already authenticated or not
      if (code === UninitializedServerErrorCode.UntrustedMachine) {
        showClient('untrusted');
      } else if (code === UninitializedServerErrorCode.ReadyToInitialize) {
        showClient('init');
      } else {
        bootstrapCheckAutoLogin(code, nextUrl);
      }

    }

    function bootstrapCheckAutoLogin(code: number, nextUrl: string) {
      if (shouldAutoLogin(nextUrl)) {
        let autoLoginResultReturned: boolean = false;
        // Go to interstitial page that may be showing 'Signing In' spinner if auto login result is not returned yet.
        // When the app is running in the background (user switched to other browser tab), the defer async cb is not
        // guaranteed to be called immediately after current call stack. To avoid getting stuck in the interstitial page,
        // do not go to interstitial page if auto login result has been returned.
        _.defer(() => {
          if (!autoLoginResultReturned) {
            showClient('interstitial');
          }
        });
        getAuthenticationServiceInstance().autoLogin().then(() => {
          autoLoginResultReturned = true;
          getAppStore().dispatch(setRootAdminUiState('default'));

          // Go to site picker if destination site is ambiguous
          if (isRootUrl() && server.isSessionMultiSite()) {
            LoginUrlsWrapper.navigateToLoginSitePicker();
          } else {
            bootstrapCheckRedirects(code, nextUrl);
          }
        }, (errorCode: number) => {
          autoLoginResultReturned = true;
          getAppStore().dispatch(setRootAdminUiState('default'));
          bootstrapCheckRedirects(errorCode, nextUrl);
        });
      } else {
        bootstrapCheckRedirects(code,  nextUrl);
      }
    }

    function bootstrapCheckRedirects(code: number, nextUrl: string) {
      if (maybeVizRedirect(nextUrl, true)) {
        // maybeVizRedirect handles the redirect
      } else if (shouldRedirectToLogin(nextUrl)) {
        showClient('default');
        redirectToLogin(code);
      } else if (shouldRedirectToSitePicker({
        isSuccessfulSamlLogin: Helpers.BooleanQueryParamRegex.test($location.search()[UrlParamKeys.IsFromSaml])
      })) {
        showClient('default');
        LoginUrlsWrapper.navigateToLoginSitePicker();
      } else {
        // Retry $locationChange
        showClient('default');
      }
    }

    $($window).on('load', () => {
      // Record page load time if the browser supports the Navigation Timing API
      // see: http://caniuse.com/#feat=nav-timing

      // Using $timeout to defer execution until after window.onload so that:
      // 1) we aren't blocking onload 2) we have the loadEventEnd stat from the Navigation Timing API
      $timeout(() => {
        // 'performance' may be null if the browser does not support the Navigation Timing API
        // 'timing' may be null if the browser user has disabled this feature
        //         (as per http://www.w3.org/TR/navigation-timing/#processing-model)
        if ($window.performance && $window.performance.timing) {
          server.recordNavigationTiming($window.performance.timing);
        }
      });
    });

    function editingDeviceSpecificAlert() {
      return UrlParamKeys.DeviceType in $location.search() && UrlParamKeys.EditDataAlert in $location.search();
    }

    $rootScope.$on('$locationChangeStart', (event: ng.IAngularEvent, newUrl: string, oldUrl: string) => {
      if (SessionService.isSessionStateUnknown()) {
        // We've just loaded the app and session state is unknown. We can't decide what
        // to do until we know the session state, so prevent the location change. This
        // will prevent the app from entering the ui router state machine.
        event.preventDefault();
        server.bootstrapSession().then((code: number) => bootstrapCheckInitialAdmin(code, newUrl));
      } else if(maybeVizRedirect(newUrl, VizService.shouldRedirectWithoutAddingToHistory(oldUrl), event) && (!editingDeviceSpecificAlert())) {
        event.preventDefault();
      } else if (shouldRedirectToLogin(newUrl)) {
        event.preventDefault();
        redirectToLogin(null);
      }
    });

    function isOnSameViewPath(newUrl: string, oldUrl: string): boolean {
      const newPath = getVizPath(newUrl);
      const oldPath = getVizPath(oldUrl);
      if (newPath === null || oldPath === null) {
        return false;
      }
      return (newPath === oldPath);
    }

    ToastWidget.initToaster({marginTop: 15});

    // Dismiss modal dialogs on location change e.g. if the user clicks the back button
    // TFS827926 - Lightbox (modal) closes when the InstanceId is changed after load.
    // Modals should only close when we're going to a different page.
    // AFAIK, the only place the $location search properties are changed is in viewing a viz.
    $rootScope.$on('$locationChangeStart', (event: ng.IAngularEvent, newUrl: string, oldUrl: string) => {
      if (!isOnSameViewPath(newUrl, oldUrl)) {
        modalService.dismissAll('location change');
        ReactModalService.dismissAll('location change');
        ToasterService.instance.closeAllToastsNotMarkedAsKeep();
      }
    });

    $rootScope.$on('$locationChangeStart', (event: ng.IAngularEvent, newUrl: string, oldUrl: string) => {
      if(shouldSetPreviousUrl(oldUrl, newUrl, VizService)) {
        // setting the previous url here keeps the sorts and filter parameters in the url
        getAppStore().dispatch(setPreviousUrl(oldUrl));
      }
    });

    // Keep html lang attribute synchronized with the current language
    $rootScope.$watch(() => L10n.language, () => {
      if (L10n.language) {
        angular.element('html')
          .attr('lang', L10n.language)
          .attr('xml:lang', L10n.language);
      }
    });
  }
]);
